import { inject } from '@angular/core';
import { RedirectCommand, Router } from '@angular/router';
import { catchError, EMPTY, from, mergeMap, Observable, of } from 'rxjs';

import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';

interface IModelResponse<T> {
    data?: { modelDto?: T | null } | null;
    error?: unknown;
}

// Gedeelde afhandeling voor de model-resolvers (aanroepen in de resolver zelf: injection context).
// - Model aanwezig → doorgeven; de navigatie gaat door.
// - Geen model → de pagina wordt niet aangemaakt (geen flits, geen paginawissel):
//   - binnen de app: navigatie annuleren — een resolver die zonder waarde eindigt, annuleert de
//     navigatie (Angular router: NavigationCancellationCode.NoDataFromResolver); je blijft waar je was;
//   - eerste navigatie (deep link/F5): er is nog geen pagina om op te blijven, dus doorsturen naar
//     fallbackPath (standaard Home) i.p.v. een leeg scherm.
// - Melding: bij een serverfout (403/404/5xx) toont de centrale foutafhandeling (ErrorReportingService)
//   die al; alleen een "geslaagde" response zonder model krijgt hier een eigen melding.
export function modelResolve$<T>(
    request: Promise<IModelResponse<T>>,
    fallbackPath: string = `/${routePage.home}`,
): Observable<T | RedirectCommand> {
    const __router = inject(Router);
    const __notificationService = inject(NotificationService);

    const __noModel$ = (): Observable<RedirectCommand> =>
        __router.navigated ? EMPTY : of(new RedirectCommand(__router.parseUrl(fallbackPath)));

    return from(request).pipe(
        mergeMap(({ data, error }) => {
            const __model = data?.modelDto;
            if (__model) return of(__model);

            if (!error) {
                __notificationService.pushNotification({ severity: 'error', text: 'De gegevens konden niet worden geladen.' });
            }
            return __noModel$();
        }),
        // Netwerkfout (promise rejected): ApiService-middleware rapporteert die al centraal.
        catchError(() => __noModel$()),
    );
}
